import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Icon } from '../tokens';
import { parseCSV, slugify } from '../utils/csvParser';
import './BulkUpload.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Catálogos canónicos del modelo de datos (mismos que Upload.js / Listing model).
const BRANDS = [
  'Auto Art', 'Auto World', 'BBR', 'Kaido House', 'Burago', 'Corgi', 'Colección Fasciculo', 'CMC', 'D Model', 'Franklin Mint',
  'Greenlight Collectibles', 'Hobby Japan', 'Hot Wheels', 'Inno Models', 'Jada Toys', 'Kyosho', 'Maisto',
  'Majorette', 'Matchbox', 'Mini GT', 'M2 Machines', 'Norev', 'QubeCarz', 'Revell', 'Schuco',
  'Solido', 'Tamiya', 'Tarmac', 'Time Micro', 'Tomica', 'Top Speed', 'Welly', 'Pop Race',
  'Ignition Model', 'Shelby', 'Trends Hobby', 'Other',
];
const CATEGORIES = [
  'American Muscle', 'Classic Muscle', 'Concept Cars', 'Custom Cars', 'Dragsters',
  'Electric Cars', 'Exotics', 'Formula 1', 'Hot Rods', 'IndyCar', 'JDM',
  'Movie & TV Cars', 'NASCAR', 'Off-Road', 'Police & Fire', 'Race Cars',
  'Rally Cars', 'Supercars', 'Trucks & SUVs', 'Vintage Cars', 'Other',
];
const MATERIALS = ['Aluminum', 'Ceramic', 'Die-cast metal', 'Fiberglass', 'Paper', 'Plastic', 'Resin', 'Rubber', 'Vinyl', 'Wood'];
const SCALES = ['1/18', '1/24', '1/32', '1/43', '1/64', '1/87'];
const ACTIONS = ['Mostrar', 'Venta', 'Cambio'];
const REQUIRED_COLS = ['name', 'brand', 'category', 'material', 'scale', 'action', 'sku', 'price', 'units', 'launchyear', 'description'];

// Mismas opciones de compresión que Dropzone.jsx para que el backend no rechace.
const COMPRESSION_OPTS = { maxSizeMB: 0.3, maxWidthOrHeight: 1600, useWebWorker: true, initialQuality: 0.82 };
const MAX_IMAGES_PER_PIECE = 6;
const UPLOAD_CONCURRENCY = 3;

// Quita solo la extensión. Mantenemos el resto literal porque el SKU puede
// contener guiones+dígitos (p.ej. "HW-001"). El matcher decide el indice por
// lookup explícito: `{sku}`, `{sku}-1`, `{sku}-2`, …
const stripExt = (name) => name.replace(/\.[^.]+$/, '');

// Validación a nivel de fila. Devuelve { ok, errors: string[] }.
function validateRow(row) {
  const errors = [];
  for (const col of REQUIRED_COLS) {
    if (!row[col] || row[col].trim() === '') errors.push(`Falta "${col}"`);
  }
  if (row.brand && !BRANDS.includes(row.brand))         errors.push(`brand "${row.brand}" inválida`);
  if (row.category && !CATEGORIES.includes(row.category)) errors.push(`category "${row.category}" inválida`);
  if (row.material && !MATERIALS.includes(row.material)) errors.push(`material "${row.material}" inválido`);
  if (row.scale && !SCALES.includes(row.scale))         errors.push(`scale "${row.scale}" inválida`);
  if (row.action && !ACTIONS.includes(row.action))       errors.push(`action "${row.action}" inválida`);
  if (row.price && Number.isNaN(Number(row.price)))       errors.push('price no numérico');
  if (row.units && Number.isNaN(Number(row.units)))       errors.push('units no numérico');
  if (row.launchyear && Number.isNaN(Number(row.launchyear))) errors.push('launchYear no numérico');
  return { ok: errors.length === 0, errors };
}

// Asocia imágenes a filas. Indexa cada imagen por su filename sin extensión
// (lowercased). Para cada fila resuelve: primero `{sku}`, luego `{sku}-1` …
// `{sku}-N` mientras existan. Fallback final: match por slug del `name`.
function matchImagesToRows(rows, images) {
  const byName = new Map();
  for (const f of images) {
    const k = stripExt(f.name).toLowerCase();
    if (!byName.has(k)) byName.set(k, f); // primera gana ante duplicado
  }
  return rows.map((row) => {
    const sku = (row.sku || '').toLowerCase().trim();
    const out = [];
    if (sku) {
      const exact = byName.get(sku);
      if (exact) out.push(exact);
      for (let i = 1; i <= MAX_IMAGES_PER_PIECE; i++) {
        const f = byName.get(`${sku}-${i}`);
        if (f) out.push(f);
        if (out.length >= MAX_IMAGES_PER_PIECE) break;
      }
    }
    if (out.length === 0) {
      const slug = slugify(row.name);
      if (slug) {
        for (const [k, f] of byName) {
          if (slugify(k) === slug) { out.push(f); if (out.length >= MAX_IMAGES_PER_PIECE) break; }
        }
      }
    }
    return out;
  });
}

export default function BulkUpload() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [csvFile, setCsvFile] = useState(null);
  const [csvRows, setCsvRows] = useState([]);    // [{ name, brand, ... }]
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvError, setCsvError] = useState('');

  const [images, setImages] = useState([]);       // File[] originales
  const [phase, setPhase] = useState('idle');     // idle | validating | uploading | done
  const [progress, setProgress] = useState({ done: 0, total: 0, current: '' });
  const [results, setResults] = useState([]);     // [{ row, status, message, publicationId? }]

  // ── CSV dropzone ───────────────────────────────────────────
  const onDropCsv = useCallback((accepted) => {
    setCsvError(''); setResults([]); setPhase('idle');
    const file = accepted[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const { headers, rows } = parseCSV(reader.result);
        if (!headers.length) { setCsvError('CSV vacío o sin encabezados'); return; }
        const headerSet = new Set(headers.map((h) => h.toLowerCase()));
        const missing = REQUIRED_COLS.filter((c) => !headerSet.has(c));
        if (missing.length) {
          setCsvError(`Faltan columnas obligatorias: ${missing.join(', ')}`);
          return;
        }
        setCsvFile(file);
        setCsvHeaders(headers);
        setCsvRows(rows);
      } catch (err) { setCsvError(`No se pudo leer el CSV: ${err.message}`); }
    };
    reader.onerror = () => setCsvError('No se pudo leer el archivo');
    reader.readAsText(file, 'utf-8');
  }, []);

  const csvDz = useDropzone({
    onDrop: onDropCsv,
    accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv'] },
    maxFiles: 1,
    multiple: false,
    disabled: phase === 'uploading',
  });

  // ── Images dropzone ────────────────────────────────────────
  const onDropImages = useCallback((accepted) => {
    setResults([]); setPhase('idle');
    setImages((prev) => {
      // Dedupe por (name, size) para evitar duplicados accidentales.
      const key = (f) => `${f.name}:${f.size}`;
      const seen = new Set(prev.map(key));
      return [...prev, ...accepted.filter((f) => !seen.has(key(f)))];
    });
  }, []);

  const imgDz = useDropzone({
    onDrop: onDropImages,
    accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [] },
    multiple: true,
    disabled: phase === 'uploading',
  });

  // ── Matching + validación derivada ─────────────────────────
  const validation = useMemo(() => {
    if (!csvRows.length) return null;
    const matched = matchImagesToRows(csvRows, images);
    const rowReports = csvRows.map((row, idx) => {
      const fieldCheck = validateRow(row);
      const imgs = matched[idx];
      const errors = [...fieldCheck.errors];
      if (!imgs.length) errors.push('Sin imagen asociada (filename debe iniciar con el SKU)');
      return { row, images: imgs, errors };
    });
    const usedImageKeys = new Set();
    matched.forEach((arr) => arr.forEach((f) => usedImageKeys.add(`${f.name}:${f.size}`)));
    const orphanImages = images.filter((f) => !usedImageKeys.has(`${f.name}:${f.size}`));
    const blocking = rowReports.filter((r) => r.errors.length > 0);
    return { rowReports, blocking, orphanImages };
  }, [csvRows, images]);

  const canUpload = !!user && validation && validation.blocking.length === 0 && phase !== 'uploading';

  // ── Upload loop con concurrencia limitada ──────────────────
  const uploadOne = async (report) => {
    const r = report.row;
    // Comprimir cada imagen <300KB antes de subir (mismo pipeline que Dropzone).
    const compressed = await Promise.all(
      report.images.map(async (f) => {
        if (f.size <= COMPRESSION_OPTS.maxSizeMB * 1024 * 1024) return f;
        try { return await imageCompression(f, COMPRESSION_OPTS); }
        catch { return f; }
      }),
    );
    const fd = new FormData();
    compressed.forEach((f) => fd.append('images', f));
    fd.append('name',        r.name);
    fd.append('brand',       r.brand);
    fd.append('category',    r.category);
    fd.append('material',    r.material);
    fd.append('scale',       r.scale);
    fd.append('action',      r.action);
    fd.append('sku',         r.sku);
    if (r.description) fd.append('description', r.description);
    if (r.launchyear)  fd.append('launchYear',  r.launchyear);
    if (r.units)       fd.append('units',       r.units);
    fd.append('status', 'active');
    fd.append('price', JSON.stringify({
      amount: Number(r.price),
      currency: (r.pricecurrency || 'MXN').toUpperCase(),
      negotiable: false,
    }));
    const { data } = await axios.post(`${API}/api/publications`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    // La primera pieza en venta de un Premium abre su solicitud de vendedor.
    if (data.sellerRequest === 'created') refreshUser();
    return data.publication?._id;
  };

  const runUpload = async () => {
    if (!validation || !canUpload) return;
    const items = validation.rowReports;
    setPhase('uploading');
    setProgress({ done: 0, total: items.length, current: '' });
    const out = new Array(items.length);
    const state = { cursor: 0 };

    const worker = async () => {
      while (true) {
        const i = state.cursor++;
        if (i >= items.length) return;
        const item = items[i];
        setProgress((p) => ({ ...p, current: item.row.name || item.row.sku }));
        try {
          const id = await uploadOne(item);
          out[i] = { row: item.row, status: 'ok', publicationId: id };
        } catch (err) {
          out[i] = { row: item.row, status: 'error', message: err.response?.data?.message || err.message };
        } finally {
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        }
      }
    };

    await Promise.all(Array.from({ length: UPLOAD_CONCURRENCY }, () => worker()));
    setResults(out);
    setPhase('done');
  };

  const reset = () => {
    setCsvFile(null); setCsvRows([]); setCsvHeaders([]); setCsvError('');
    setImages([]); setResults([]); setPhase('idle'); setProgress({ done: 0, total: 0, current: '' });
  };

  const successCount = results.filter((r) => r?.status === 'ok').length;
  const errorCount   = results.filter((r) => r?.status === 'error').length;
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="bulk-page">
      <div className="bulk-body container">
        <div className="bulk-main">
          <h1 className="bulk-h1">Subir varias piezas</h1>
          <p className="bulk-sub">
            Carga un CSV con las columnas requeridas y selecciona todas las imágenes. El sistema
            asociará cada imagen a su fila usando el <strong>SKU</strong> como nombre de archivo
            (ej. <code>HW-001.jpg</code>, <code>HW-001-2.jpg</code>).
          </p>

          <div className="bulk-grid">
            {/* CSV */}
            <div>
              <p className="bulk-label">1. Archivo CSV</p>
              <div {...csvDz.getRootProps()} className={`bulk-dz${csvDz.isDragActive ? ' bulk-dz--over' : ''}`}>
                <input {...csvDz.getInputProps()} />
                {csvFile ? (
                  <div className="bulk-dz__filled">
                    <div className="bulk-dz__icon">{Icon.doc(20)}</div>
                    <div>
                      <p className="bulk-dz__title">{csvFile.name}</p>
                      <p className="bulk-dz__sub">{csvRows.length} filas · {csvHeaders.length} columnas</p>
                    </div>
                  </div>
                ) : (
                  <div className="bulk-dz__empty">
                    <div className="bulk-dz__icon">{Icon.doc(20)}</div>
                    <p className="bulk-dz__title">Suelta el .csv aquí</p>
                    <p className="bulk-dz__sub">o haz click para elegir</p>
                  </div>
                )}
              </div>
              {csvError && <p className="bulk-err">{csvError}</p>}
              <p className="bulk-hint">
                Columnas requeridas: <code>{REQUIRED_COLS.join(', ')}</code>. Opcional:
                <code> priceCurrency</code> (default MXN).
              </p>
            </div>

            {/* Images */}
            <div>
              <p className="bulk-label">2. Imágenes</p>
              <div {...imgDz.getRootProps()} className={`bulk-dz${imgDz.isDragActive ? ' bulk-dz--over' : ''}`}>
                <input {...imgDz.getInputProps()} />
                {images.length ? (
                  <div className="bulk-dz__filled">
                    <div className="bulk-dz__icon">{Icon.camera(20)}</div>
                    <div>
                      <p className="bulk-dz__title">{images.length} imágenes</p>
                      <p className="bulk-dz__sub">Click para agregar más</p>
                    </div>
                  </div>
                ) : (
                  <div className="bulk-dz__empty">
                    <div className="bulk-dz__icon">{Icon.camera(20)}</div>
                    <p className="bulk-dz__title">Suelta las imágenes aquí</p>
                    <p className="bulk-dz__sub">JPG, PNG o WebP — múltiples</p>
                  </div>
                )}
              </div>
              {images.length > 0 && (
                <button type="button" className="bulk-link" onClick={() => setImages([])} disabled={phase === 'uploading'}>
                  Quitar todas las imágenes
                </button>
              )}
            </div>
          </div>

          {/* Validación */}
          {validation && (
            <div className="bulk-summary">
              <div className="bulk-summary__row">
                <span className={`bulk-pill${validation.blocking.length === 0 ? ' bulk-pill--ok' : ' bulk-pill--err'}`}>
                  {validation.rowReports.length - validation.blocking.length} / {validation.rowReports.length} listas
                </span>
                {validation.blocking.length > 0 && (
                  <span className="bulk-pill bulk-pill--err">{validation.blocking.length} con errores</span>
                )}
                {validation.orphanImages.length > 0 && (
                  <span className="bulk-pill bulk-pill--warn">
                    {validation.orphanImages.length} imágenes sin fila
                  </span>
                )}
              </div>

              {validation.blocking.length > 0 && phase !== 'done' && (
                <div className="bulk-errors">
                  <p className="bulk-errors__title">Corrige antes de procesar:</p>
                  <ul>
                    {validation.blocking.slice(0, 10).map((r, i) => (
                      <li key={i}>
                        <strong>{r.row.sku || r.row.name || `Fila ${i + 1}`}</strong>: {r.errors.join('; ')}
                      </li>
                    ))}
                    {validation.blocking.length > 10 && (
                      <li>… y {validation.blocking.length - 10} más</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Preview tabla */}
              {phase !== 'done' && (
                <div className="bulk-table-wrap">
                  <table className="bulk-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>#</th>
                        <th>SKU</th>
                        <th>Nombre</th>
                        <th>Marca</th>
                        <th>Precio</th>
                        <th style={{ width: 60 }}>Imgs</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validation.rowReports.slice(0, 50).map((r, i) => (
                        <tr key={i} className={r.errors.length ? 'bulk-row--err' : ''}>
                          <td>{i + 1}</td>
                          <td>{r.row.sku}</td>
                          <td>{r.row.name}</td>
                          <td>{r.row.brand}</td>
                          <td>{r.row.price ? `${r.row.price} ${(r.row.pricecurrency || 'MXN').toUpperCase()}` : ''}</td>
                          <td>{r.images.length}</td>
                          <td>{r.errors.length ? `⚠ ${r.errors[0]}` : '✓ OK'}</td>
                        </tr>
                      ))}
                      {validation.rowReports.length > 50 && (
                        <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--muted)' }}>
                          … {validation.rowReports.length - 50} filas más
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Progreso */}
          {phase === 'uploading' && (
            <div className="bulk-progress">
              <div className="bulk-progress__bar"><div className="bulk-progress__fill" style={{ width: `${pct}%` }} /></div>
              <p className="bulk-progress__txt">
                Procesando pieza {progress.done} de {progress.total}
                {progress.current && <> · <em>{progress.current}</em></>}
              </p>
            </div>
          )}

          {/* Resumen final */}
          {phase === 'done' && (
            <div className="bulk-summary">
              <div className="bulk-summary__row">
                <span className="bulk-pill bulk-pill--ok">{successCount} subidas</span>
                {errorCount > 0 && <span className="bulk-pill bulk-pill--err">{errorCount} con error</span>}
              </div>
              {errorCount > 0 && (
                <div className="bulk-errors">
                  <p className="bulk-errors__title">Errores al subir:</p>
                  <ul>
                    {results.filter((r) => r.status === 'error').slice(0, 20).map((r, i) => (
                      <li key={i}><strong>{r.row.sku || r.row.name}</strong>: {r.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Acciones */}
          <div className="bulk-actions">
            {phase !== 'done' && (
              <button className="btn-teal" onClick={runUpload} disabled={!canUpload}>
                {phase === 'uploading' ? 'Subiendo…' : `Procesar ${validation?.rowReports.length || 0} piezas`}
              </button>
            )}
            {phase === 'done' && (
              <>
                <button className="btn-teal" onClick={() => navigate('/profile')}>Ver mi perfil</button>
                <button className="btn-ghost" onClick={reset}>Nueva carga</button>
              </>
            )}
            {phase !== 'uploading' && phase !== 'done' && (csvFile || images.length > 0) && (
              <button className="btn-ghost" onClick={reset}>Limpiar</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
